import { ChatSettings, ContentLanguage } from 'models/settings';
import { convertToContentLanguage } from './languageUtils';

export type ChannelType = 'booking_site' | 'map' | 'guest_portal' | 'external_embed';

export interface ChatVisibilityContext {
  channelType: ChannelType;
  channelId?: string;
  language?: ContentLanguage;
  currentUrl?: string;
}

export function shouldShowChatChannel(
  chatSettings: ChatSettings | undefined,
  context: ChatVisibilityContext,
  existingVisibility?: boolean
): boolean {
  // If existing visibility is explicitly set to true, always show
  if (existingVisibility === true) {
    return true;
  }

  // Check channel-specific settings
  const channelSettings = chatSettings?.channel_settings;
  if (!channelSettings) {
    // No channel settings - use existing visibility or default to true for external_embed
    return existingVisibility ?? context.channelType === 'external_embed';
  }

  const channels = channelSettings[context.channelType];
  if (!channels || channels.length === 0) {
    // No specific channel settings for this type - use existing visibility or default to true for external_embed
    return existingVisibility ?? context.channelType === 'external_embed';
  }

  // Find matching channel
  let matchingChannel;

  switch (context.channelType) {
    case 'booking_site':
      // Match by language code - normalize both for comparison
      const normalizedInputLanguage = context.language
        ? convertToContentLanguage(context.language)
        : undefined;
      matchingChannel = channels.find(
        (channel) => convertToContentLanguage(channel.channel_id) === normalizedInputLanguage
      );
      break;

    case 'map':
    case 'guest_portal':
      // Match by exact ID
      matchingChannel = channels.find((channel) => channel.channel_id === context.channelId);
      break;

    case 'external_embed':
      // Match by substring in current URL
      if (context.currentUrl) {
        matchingChannel = channels.find((channel) =>
          context.currentUrl!.includes(channel.channel_id)
        );
      }
      break;
  }

  if (matchingChannel) {
    return matchingChannel.enabled;
  }

  // No matching channel found - use existing visibility or default to true for external_embed
  return existingVisibility ?? context.channelType === 'external_embed';
}

export function checkBookingSiteChatVisibility(
  chatSettings: ChatSettings | undefined,
  language: ContentLanguage | string,
  enableBookingWebsiteChat?: boolean
): boolean {
  // Normalize the language to ContentLanguage format
  const normalizedLanguage = convertToContentLanguage(language) as ContentLanguage;

  return shouldShowChatChannel(
    chatSettings,
    { channelType: 'booking_site', language: normalizedLanguage },
    enableBookingWebsiteChat
  );
}

export function checkGuestPortalChatVisibility(
  chatSettings: ChatSettings | undefined,
  guestPortalId: string,
  existingVisibility?: boolean
): boolean {
  return shouldShowChatChannel(
    chatSettings,
    { channelType: 'guest_portal', channelId: guestPortalId },
    existingVisibility
  );
}

export function checkMapChatVisibility(
  chatSettings: ChatSettings | undefined,
  mapId: string,
  existingVisibility?: boolean
): boolean {
  return shouldShowChatChannel(
    chatSettings,
    { channelType: 'map', channelId: mapId },
    existingVisibility
  );
}

export function checkExternalEmbedChatVisibility(
  chatSettings: ChatSettings | undefined,
  currentUrl: string
): boolean {
  return shouldShowChatChannel(
    chatSettings,
    { channelType: 'external_embed', currentUrl },
    undefined // No existing visibility for external embeds
  );
}
